import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Stars } from "@/components/reviews/Stars";

export const ReviewDialog = ({ open, onOpenChange, bookingId = null, defaultName = "", onSubmitted }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: defaultName, rating: 0, comment: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ name: defaultName, rating: 0, comment: "" });
      setDone(false);
      setError("");
    }
  }, [open, defaultName]);

  const valid = form.name.trim().length >= 2 && form.rating > 0 && form.comment.trim().length >= 3;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/reviews", { ...form, booking_id: bookingId });
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border border-[#D4AF37]/40 text-slate-200 max-w-lg" data-testid="review-dialog">
        {done ? (
          <div className="text-center py-6" data-testid="review-thank-you">
            <CheckCircle2 size={40} className="mx-auto text-[#D4AF37] mb-4" />
            <p className="font-display text-xl text-[#F3DC8B] mb-2">{t("rv.thanks")}</p>
            <button onClick={() => onOpenChange(false)} className="btn-ghost-gold px-6 py-2.5 mt-4" data-testid="review-close-button">{t("rv.close")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl gold-text">{t("rv.title")}</DialogTitle>
              <DialogDescription className="text-slate-400">{t("rv.sub")}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-2"><Label>{t("rv.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} className="field-sacred h-11" data-testid="review-name-input" /></div>
            <div className="grid gap-2"><Label>{t("rv.rating")}</Label>
              <Stars value={form.rating} size={30} onChange={(rating) => setForm({ ...form, rating })} testid="review-star" /></div>
            <div className="grid gap-2"><Label>{t("rv.comment")}</Label>
              <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} maxLength={1000} className="field-sacred min-h-[120px]" data-testid="review-comment-input" /></div>
            {error && <p className="text-sm text-red-400" data-testid="review-error">{error}</p>}
            <button type="submit" disabled={!valid || busy} className="btn-gold h-11 flex items-center justify-center gap-2" data-testid="review-submit-button">
              {busy && <Loader2 size={16} className="animate-spin" />} {t("rv.submit")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
